import React, { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { 
  PlusIcon, 
  PencilIcon,
  CalendarIcon,
  CurrencyPoundIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline'

interface Subscription {
  id: string
  sku: string
  productName: string
  quantity: number
  unitPrice: number
  totalPrice: number
  currency: string
  term: string
  billingCadence: string
  status: 'active' | 'suspended' | 'cancelled' | 'expired'
  startedAt: string
  renewsAt: string
  autoRenew: boolean
}

const Subscriptions: React.FC = () => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'active' | 'expiring'>('all')

  useEffect(() => {
    fetchSubscriptions()
  }, [])

  const fetchSubscriptions = async () => {
    try {
      // Mock data for now - will be replaced with real API calls
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      const mockSubscriptions: Subscription[] = [
        {
          id: 'sub_001',
          sku: 'CFQ7TTC0LDG0',
          productName: 'Microsoft 365 Business Premium',
          quantity: 25,
          unitPrice: 18.50,
          totalPrice: 462.50,
          currency: 'GBP',
          term: 'monthly',
          billingCadence: 'monthly',
          status: 'active',
          startedAt: '2024-01-15T00:00:00Z',
          renewsAt: '2025-02-15T00:00:00Z',
          autoRenew: true
        },
        {
          id: 'sub_002',
          sku: 'CFQ7TTC0LCRX',
          productName: 'Microsoft 365 E3',
          quantity: 5,
          unitPrice: 28.90,
          totalPrice: 144.50,
          currency: 'GBP',
          term: 'monthly',
          billingCadence: 'monthly',
          status: 'active',
          startedAt: '2024-06-01T00:00:00Z',
          renewsAt: '2025-01-01T00:00:00Z',
          autoRenew: true
        },
        {
          id: 'sub_003',
          sku: 'AZURE_P1',
          productName: 'Azure Pay-As-You-Go',
          quantity: 1,
          unitPrice: 45.00,
          totalPrice: 45.00,
          currency: 'GBP',
          term: 'monthly',
          billingCadence: 'monthly',
          status: 'active',
          startedAt: '2024-03-01T00:00:00Z',
          renewsAt: '2025-03-01T00:00:00Z',
          autoRenew: true
        }
      ]
      
      setSubscriptions(mockSubscriptions)
    } catch (error) {
      console.error('Failed to fetch subscriptions:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatPrice = (price: number, currency: string = 'GBP') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency
    }).format(price)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const getStatusBadge = (status: string) => {
    const badges = {
      active: 'status-active',
      suspended: 'status-pending',
      cancelled: 'status-error',
      expired: 'status-inactive'
    }
    return badges[status as keyof typeof badges] || 'status-inactive'
  }

  const filteredSubscriptions = subscriptions.filter(sub => {
    if (filter === 'active') return sub.status === 'active'
    if (filter === 'expiring') {
      const renewDate = new Date(sub.renewsAt)
      const thirtyDaysFromNow = new Date()
      thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30)
      return renewDate <= thirtyDaysFromNow && sub.status === 'active'
    }
    return true
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-vcns-accent"></div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>Subscriptions - VCNS Support Portal</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Subscriptions</h1>
            <p className="mt-1 text-sm text-gray-500">
              Manage your Microsoft 365, Azure, and other cloud services
            </p>
          </div>
          <div className="mt-4 sm:mt-0">
            <a
              href="https://vcns.tech/mpn/csp/catalog"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Add New Service
            </a>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex space-x-4">
              <button
                onClick={() => setFilter('all')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  filter === 'all' 
                    ? 'bg-vcns-accent bg-opacity-10 text-vcns-secondary' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                All ({subscriptions.length})
              </button>
              <button
                onClick={() => setFilter('active')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  filter === 'active' 
                    ? 'bg-vcns-accent bg-opacity-10 text-vcns-secondary' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Active ({subscriptions.filter(s => s.status === 'active').length})
              </button>
              <button
                onClick={() => setFilter('expiring')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  filter === 'expiring' 
                    ? 'bg-vcns-accent bg-opacity-10 text-vcns-secondary' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Expiring Soon (1)
              </button>
            </div>
          </div>
        </div>

        {/* Subscriptions List */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {filteredSubscriptions.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <BuildingOfficeIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No subscriptions found</h3>
              <p className="text-gray-500 mb-4">
                {filter === 'all' 
                  ? "You don't have any active subscriptions yet."
                  : `No subscriptions match the ${filter} filter.`
                }
              </p>
              <a
                href="https://vcns.tech/mpn/csp/catalog"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
              >
                Browse Services
              </a>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredSubscriptions.map((subscription) => (
                <div key={subscription.id} className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3">
                        <h3 className="text-lg font-medium text-gray-900 truncate">
                          {subscription.productName}
                        </h3>
                        <span className={`${getStatusBadge(subscription.status)} capitalize`}>
                          {subscription.status}
                        </span>
                      </div>
                      <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm text-gray-500">
                        <div className="flex items-center">
                          <BuildingOfficeIcon className="w-4 h-4 mr-1" />
                          {subscription.quantity} licenses
                        </div>
                        <div className="flex items-center">
                          <CurrencyPoundIcon className="w-4 h-4 mr-1" />
                          {formatPrice(subscription.totalPrice)} / {subscription.billingCadence}
                        </div>
                        <div className="flex items-center">
                          <CalendarIcon className="w-4 h-4 mr-1" />
                          Renews {formatDate(subscription.renewsAt)}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button className="btn-outline btn text-xs">
                        <PencilIcon className="w-3 h-3 mr-1" />
                        Modify
                      </button>
                    </div>
                  </div>
                  
                  {/* Details */}
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs text-gray-500">
                      <div>
                        <span className="font-medium">SKU:</span> {subscription.sku}
                      </div>
                      <div>
                        <span className="font-medium">Unit Price:</span> {formatPrice(subscription.unitPrice)}
                      </div>
                      <div>
                        <span className="font-medium">Started:</span> {formatDate(subscription.startedAt)}
                      </div>
                      <div>
                        <span className="font-medium">Auto-renew:</span> {subscription.autoRenew ? 'Yes' : 'No'}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Summary */}
        {filteredSubscriptions.length > 0 && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Monthly Summary</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900">
                    {filteredSubscriptions.filter(s => s.status === 'active').length}
                  </div>
                  <div className="text-sm text-gray-500">Active Services</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900">
                    {filteredSubscriptions.reduce((sum, s) => sum + s.quantity, 0)}
                  </div>
                  <div className="text-sm text-gray-500">Total Licenses</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900">
                    {formatPrice(filteredSubscriptions.reduce((sum, s) => sum + s.totalPrice, 0))}
                  </div>
                  <div className="text-sm text-gray-500">Monthly Cost</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default Subscriptions
